import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { TableWrapper } from './style'
import { Dimmer, Dropdown, Input, Loader, Popup, SemanticICONS } from 'semantic-ui-react'
import TableComponent from '~/components/TableComponent'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { useDebounce } from 'use-lodash-debounce';
import CustomIcon from '~/components/CustomIcon'
import { DownloadService } from '~/api/services/downloadFiles'
import { handleErrors } from '~/utils/helpers'
import i18n from '~/components/i18n'

interface Props {
  farmerGroupId: string
}

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    sorted: 'desc'
  },
  {
    name: i18n.t('Progress'),
    key: 'step',
    type: 'co2-progress',
    sorted: 'desc'
  },
  {
    name: i18n.t('Status'),
    key: 'completed',
    type: 'verified'
  },
  {
    name: i18n.t('Last login'),
    key: 'lastLogin',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Completed at'),
    key: 'completedAt',
    type: 'date',
    format: 'dddd, D MMMM YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Emission factor'),
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'number-or-value',
    unit: 'kg CO2e / kg FPCM',
    decimalScale: 2,
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const FarmersTable: React.FC<Props> = ({ farmerGroupId }) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const actions = [
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />,
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'pdf-results',
      renderActions: getActions
    }
  ]

  const completedFilterOpt = [
    {
      text: t('Data collection completed'),
      value: 1
    },
    {
      text: t('Data collection not completed'),
      value: 0
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [search, setSearch] = useState('')
  const debouncedSearchValue = useDebounce(search, 300)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    farmerGroupId,
    completed: null
  });

  const {
    isLoading: isBusyFarmers,
    data: farmers,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getFarmers(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
      farmerGroupId
    });

  useEffect(() => {
    if (farmerGroupId) {
      fetchFilteredData()
    }
  }, [farmerGroupId])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  const { filtered, rows } = farmers ? farmers : { filtered: 0, rows: [] };

  async function handleDownloadPdfResults(farmer) {
    setLoadingAction({
      type: 'pdf-results',
      id: farmer.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`co2-footprints/${id}/farmers/${farmer?.id}/get-results/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function getActions(farmer, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    switch (actionType) {
      case 'pdf-results':
        return farmer?.completed === 1 && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handleDownloadPdfResults(farmer)}
            size='large'
          />}
        />
      default:
        return
    }
  }

  return <TableWrapper>
    <Dimmer inverted active={isBusyFarmers}>
      <Loader active={isBusyFarmers} />
    </Dimmer>
    <div className='table-header'>
      <div>{t('Farmers')}</div>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selectOnBlur={false}
        options={completedFilterOpt}
        selection
        placeholder={t('Data collection completed?')}
        clearable
        value={dataFiltered.completed}
        onChange={(e, data) => {
          setDataFiltered({
            ...dataFiltered,
            //@ts-ignore per backend request
            completed: isNaN(data.value) ? null : data.value
          })
        }}
      />
    </div>
    <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      animated
      loadingAction={loadingAction}
      actions={actions}
      isFetchingData={isFetchingData}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  </TableWrapper>
}
export default FarmersTable